import React from 'react'
import { Box, Typography, Tooltip, IconButton } from '@mui/material'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import HistoryIcon from '@mui/icons-material/History'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import { CONTENT_ROOTS } from 'services/content'
import Link from 'gui/components/link'

export interface EditorBannerProps {
  contentId: string | number
}

const bannerSx = {
  px: 2,
  py: 1.25,
  mb: 2,
  width: '100%',
  boxSizing: 'border-box',
  bgcolor: 'grey.100',
  borderRadius: 1,
  borderLeft: '3px solid',
  borderColor: 'grey.400',
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flexWrap: 'wrap',
} as const

const bannerLeftSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flexWrap: 'wrap',
  minWidth: 0,
} as const

const bannerLinkSx = {
  ml: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  flexShrink: 0,
} as const

/** Pale red — signals deprecated / legacy editor (v1) */
const classicBannerSx = {
  px: 2,
  py: 1.25,
  mb: 2,
  width: '100%',
  boxSizing: 'border-box',
  bgcolor: '#fff0f0',
  borderRadius: 1,
  borderLeft: '3px solid',
  borderColor: 'error.light',
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flexWrap: 'wrap',
} as const

/**
 * Banner shown on V2 detail pages, linking back to the classic editor.
 */
function NewEditorBanner ({ contentId }: EditorBannerProps) {
  return (
    <Box sx={bannerSx}>
      <Box sx={bannerLeftSx}>
        <AutoAwesomeOutlinedIcon sx={{ fontSize: 18, color: 'grey.600' }} />
        <Typography variant="body1" color="text.secondary" component="span">
          New editor
        </Typography>
        <Tooltip
          title="You can open the classic (legacy) content detail page if you need the previous form or workflows."
          placement="top"
        >
          <IconButton
            size="small"
            sx={{ p: 0.25, color: 'grey.600' }}
            aria-label="Legacy editor info"
          >
            <InfoIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={bannerLinkSx}>
        <Typography variant="body1" color="text.secondary" component="span">
          ·
        </Typography>
        <Link href={`${CONTENT_ROOTS.BASE}/${contentId}`}>Open classic editor</Link>
      </Box>
    </Box>
  )
}

/**
 * Mini banner for legacy content detail pages linking to the new (V2) editor.
 * Mirrors the inline banner in video-detail.js.
 */
export function ClassicEditorBanner ({ contentId }: EditorBannerProps) {
  return (
    <Box sx={classicBannerSx}>
      <Box sx={bannerLeftSx}>
        <HistoryIcon sx={{ fontSize: 18, color: 'grey.600' }} />
        <Typography variant="body1" color="text.secondary" component="span">
          Classic editor
        </Typography>
        <Tooltip
          title="This is the legacy form which will be discontinued. If you needed to fallback to this form, please tell an Admin what failure you experienced."
          placement="top"
        >
          <IconButton
            size="small"
            sx={{ p: 0.25, color: 'grey.600' }}
            aria-label="Legacy form info"
          >
            <InfoIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={bannerLinkSx}>
        <Typography variant="body1" color="text.secondary" component="span">
          ·
        </Typography>
        <Link href={`${CONTENT_ROOTS.V2}/${contentId}`}>Try the new editor</Link>
      </Box>
    </Box>
  )
}

export default NewEditorBanner
